import { ref } from 'vue'
import { createComment, deleteComment, getCommentsByTicket, updateComments } from '@/service/comments'
import { useAccountStore } from '@/stores/account'

export function useComments() {
  const accountStore = useAccountStore()
  const comments = ref<any[]>([])
  const isAdding = ref(false)
  const loadingComments = ref(false)

  /**
   * Adds a comment to a ticket.
   * @param ticketId - Ticket identifier.
   * @param content - Content of the comment.
   * @param type - Type of comment ("INTERNAL" or "EXTERNAL").
   * @returns Promise that resolves with the created comment.
   */
  const addComment = async (ticketId: string, content: string, type: 'Internal' | 'External' = 'External'): Promise<any> => {
    if (!content.trim()) return
    isAdding.value = true
    try {
      const commentData = {
        content,
        isPrivate: type === 'Internal',
        createdBy: accountStore.user.id as string,
      }
      const { onSuccess } = createComment(ticketId, commentData)

      return new Promise((resolve, reject) => {
        onSuccess((data: any) => {
          comments.value.unshift({
            ...data,
            ...commentData,
            createdById: accountStore.user,
            createdAt: new Date().toISOString(),
          })
          resolve(data.comment)
        })
      })
    } catch (error) {
      console.error('Error while adding the comment:', error)
      throw error
    } finally {
      isAdding.value = false
    }
  }

  /**
   * Fetches the comments of a ticket and filters by type if specified.
   * @param ticketId - Ticket identifier.
   * @param filterType - Type of comment to fetch ("INTERNAL", "EXTERNAL", or "ALL" for all).
   */
  const fetchComments = async (ticketId: string) => {
    loadingComments.value = true
    try {
      const { onSuccess } = getCommentsByTicket(ticketId)

      onSuccess((data: any) => {
        comments.value = data
      })
    } catch (error) {
      console.error('Error while fetching the comments:', error)
    } finally {
      loadingComments.value = false
    }
  }

  /**
   * Deletes a comment from a ticket.
   * @param ticketId
   * @param commentId - Identifier of the comment to delete.
   */
  const deleteCommentHandler = async (ticketId: string, commentId: string) => {
    try {
      const { onSuccess } = deleteComment(ticketId, commentId)

      onSuccess(() => {
        comments.value = comments.value.filter((comment) => comment.id !== commentId)
      })
    } catch (error) {
      console.error('Error while deleting the comment:', error)
    }
  }

  /**
   * Updates the content of a comment.
   * @param ticketId
   * @param commentId - ID of the comment to update.
   * @param newContent - New content of the comment.
   */
  const updateComment = async (ticketId: string, commentId: string, newContent: string) => {
    if (!newContent.trim()) return

    try {
      const { onSuccess } = updateComments(ticketId, commentId, { content: newContent })

      onSuccess(() => {
        const index = comments.value.findIndex((c) => c.id === commentId)

        if (index !== -1) {
          comments.value[index] = {
            ...comments.value[index],
            content: newContent,
            createdAt: new Date().toISOString(),
          }
        }
      })
    } catch (error) {
      console.error('Error while updating the comment:', error)
    }
  }

  return {
    comments,
    isAdding,
    loadingComments,
    updateComment,
    addComment,
    fetchComments,
    deleteCommentHandler,
  }
}
